import { zodResolver } from "@hookform/resolvers/zod";
import {
    Box,
    CircularProgress,
    Grid,
    TextField,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { t } from "i18next";
import { useForm } from "react-hook-form";
import { z } from "zod";
import BasicDialog from "../../components/base/BasicDialog";
import { useToasts } from "../../hooks/useToasts";
import toast from "react-hot-toast"
import { SnackbarSuccess } from "../../components/base/SnackbarAlert/SnackbarSuccess";

import { useCreateBankingMutation, useBankingFilteringData } from "../../services/bankingService";

import { ControlledSelect } from "../../components/form/ControlledSelect/ControlledSelect";
import MaskedTextFieldForm from "../../components/base/MaskedTextFieldForm/MaskedTextFieldForm";

export const bankingFormSchema = z
    .object({
        banking_name: z.string(),
        banking_nr: z.string(),
        banking_kind: z.string(),
        banking_bank: z.string(),
        banking_invest: z.string(),
        banking_note: z.string().optional().nullable()
    })


export function AddBankingDialog(props) {
    const { handleSubmit, control, formState, register, reset, setValue, watch } = useForm({
        resolver: zodResolver(bankingFormSchema),
        defaultValues: {},
    });

    const { onFormErrors, onServerError } = useToasts();

    const {data: filteringData, isLoading: isFilteringDataLoading} = useBankingFilteringData(new URLSearchParams(props.isBankingForWithdrawal != undefined ? {banking_kind_for_withdrawal: props.isBankingForWithdrawal} : {}))
    const { mutate: addBanking, isPending: isCreateBanking } = useCreateBankingMutation({
        onSuccess: (data, variables, context) => {
            toast.custom(
                () => <SnackbarSuccess message={t("successfullyAdded")} />,
                {
                    position: "bottom-right",
                },
            );
            if (props.onAddBankingCallback) {
                props.onAddBankingCallback(data)
            }
            props.onClose()
        },
        onError: (error) => {
            onServerError(error);
        },
    });


    const onSubmit = (data) => {
        addBanking({data:data})
    };

    const ibanNrFormatRegex = /^\d{2}\s\d{4}\s\d{4}\s\d{4}\s\d{4}\s\d{4}\s\d{4}$/
    const isBankingNrFormatValid = watch('banking_nr') && ibanNrFormatRegex.test(watch('banking_nr'))
    const isFormValid =  watch('banking_name') && watch('banking_nr') && watch('banking_bank') && watch('banking_invest') && watch('banking_kind') && isBankingNrFormatValid

    if (isCreateBanking || isFilteringDataLoading || !filteringData) {
        return (
            <BasicDialog
                open={props.open}
                onClose={props.onClose}
                titleAlign={"center"}
                contentAlign={"center"}
                title="Loading..."
                maxWidth={"xs"}
                showDialogActions
                alwaysFullScreen={false}
            >
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            </BasicDialog>
        );
    }

    return (
        <BasicDialog
            open={props.open}
            onClose={props.onClose}
            titleAlign={"center"}
            contentAlign={"center"}
            showTitle={true}
            title={t("bankings.add_banking")}
            maxWidth={"sm"}
            
        >
            <Grid container rowSpacing={1}>
                <Grid item xs={12}>
                    <TextField
                        {...register("banking_name")}
                        fullWidth
                        disabled={false}
                        label={`${t("bankings.banking_name")}*`}
                        size="small"
                        sx={{background: "white"}}
                    />
                </Grid>
                <Grid item xs={12}>
                    <MaskedTextFieldForm
                        mask={"99 9999 9999 9999 9999 9999 9999"}
                        disabled={false}
                        onChange={(e) => setValue("banking_nr", e.target.value)}
                        value={watch("banking_nr")}
                        maskChar={"_"}                       
                        label={`${t("bankings.banking_nr")}*`}
                        showHelperText={!watch('banking_nr') ? false : !isBankingNrFormatValid}
                        FormHelperTextProps={{ sx: { color: 'red' } }}
                        helperText={t("bankings.uncorect_banking_nr")}
                    />
                </Grid>
                <Grid item xs={12}>
                    <ControlledSelect
                        control={control}
                        sx={{background: "white"}}
                        name="banking_bank"
                        label={`${t("bankings.banking_bank")}*`}
                        data={filteringData?.bank?.map(record=>({"value" : record.id, "label": record.bank_name}))}
                    />
                </Grid>
              <Grid item xs={12}>
                    <ControlledSelect
                        control={control}
                        sx={{background: "white"}}
                        name="banking_invest"
                        label={`${t("bankings.banking_invest")}*`}
                        data={filteringData?.investment?.map(record=>({"value" : record.id, "label": record.investment_name_sales}))}
                    />
                </Grid>
              <Grid item xs={12}>
                    <ControlledSelect
                        control={control}
                        sx={{background: "white"}}
                        name="banking_kind"
                        label={`${t("bankings.banking_kind")}*`}
                        data={filteringData?.banking_kind?.map(record=>({"value" : record.id, "label": record.bankingkind_name}))}
                    />
                </Grid>
               <Grid item xs={12}>
                    <TextField
                        {...register("banking_note")}
                        fullWidth
                        disabled={false}
                        label={t("bankings.banking_note")}
                        size="small"
                        sx={{background: "white"}}
                        multiline={true}
                        rows={2}
                    />
                </Grid>
                <Grid item xs={12}>
                    <LoadingButton
                        loading={isCreateBanking}
                        disabled={!isFormValid}
                        variant="contained"
                        color="primary"
                        sx={{ mt: 2 }}
                        onClick={handleSubmit(onSubmit, onFormErrors)}
                        fullWidth
                    >
                        {t("bankings.add")}
                    </LoadingButton>
                </Grid>
            </Grid>
        </BasicDialog>
    );
}
